import {
    ActionBar,
    ActionBarSeparator,
    Add,
    Button,
    Delete,
    Export,
    Print,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import { t } from "i18next";
import React from "react";

import RefreshIcon from "components/icons/RefreshIcon";
import { MAX_PRINT_REPORTS_LIMIT, MAX_RECHECK_REPORTS_LIMIT } from "components/reports/AllReportsTable";
import { AUTH_ASSET_CREATE, AUTH_LICENSE_CONSUME, AUTH_REPORT_DELETE, AUTH_REPORT_EXPORT_ALL } from "domain/authority";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    selectedCount: number;
    totalCount: number;
    deselectAll: () => void;
    selectAll: () => void;
    openExportModal: () => void;
    openRegisterAssetModal: () => void;
    openPrintModal: () => void;
    openAutopilotRecheckModal: () => void;
    openDeleteModal: () => void;
    isPrintDisabled: boolean;
    selectionCountOverRecheckLimit: boolean;
    hasValidLicensesForRecheck: boolean;
    isLoading: boolean;
    isReportWithAutopilotStateSelected: boolean;
    isErasureReportSelected: boolean;
}

export const ReportsTableActionBar = (props: Props) => {
    const hasExportAuthorities = userSessionService.userHasAllAuthorities([AUTH_REPORT_EXPORT_ALL]);
    const hasDeleteAuthorities = userSessionService.userHasAllAuthorities([AUTH_REPORT_DELETE]);
    const hasAutopilotRecheckAuthorities = userSessionService.userHasAllAuthorities([AUTH_LICENSE_CONSUME]);
    const hasRegisterAssetAuthorities = userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]);
    const hasAssetManagementFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
    const isAssetManagementEnabled = useFeature(FLAG_ASSET_MANAGEMENT);

    return (
        <ActionBar data-testid={testIds.common.primaryView.table.actionBar.itself}>
            <TextBlock disableBottomSpacing testId={testIds.common.primaryView.table.actionBar.selectedCount}>
                {t("DeleteReport.information.reportsSelected", { total: props.selectedCount })}
            </TextBlock>

            <ActionBarSeparator />

            <Button
                variant="Tertiary"
                disabled={props.isLoading}
                onClick={props.deselectAll}
                testId={testIds.common.primaryView.table.actionBar.deselectButton}
            >
                {t("DeleteReport.information.deselect")}
            </Button>

            <Button
                variant="Tertiary"
                disabled={props.isLoading}
                onClick={props.selectAll}
                testId={testIds.common.primaryView.table.actionBar.selectAllButton}
            >
                {t("AssetManagement.selectAll", { total: props.totalCount })}
            </Button>

            <ActionBarSeparator />

            {hasExportAuthorities && !hasTenantCookie() && (
                <Button
                    variant="Tertiary"
                    disabled={props.isLoading}
                    icon={<Export color={resolveColor("--theme-icon-fill-default")} />}
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.REPORTS,
                            action: Action.EXPORT_SELECTED_REPORTS,
                        });
                        props.openExportModal();
                    }}
                    testId={testIds.common.primaryView.table.actionBar.button}
                    dataType="EXPORT"
                >
                    {t("Common.export")}
                </Button>
            )}

            {hasAssetManagementFeatureLicense && isAssetManagementEnabled && hasRegisterAssetAuthorities && (
                <Button
                    variant="Tertiary"
                    disabled={props.isLoading}
                    icon={<Add color={resolveColor("--theme-icon-fill-default")} />}
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ASSET_MANAGEMENT,
                            action: Action.ADD_ASSET_FROM_REPORT,
                        });
                        props.openRegisterAssetModal();
                    }}
                    testId={testIds.common.primaryView.table.actionBar.button}
                    dataType="REGISTER_ASSET"
                >
                    {props.selectedCount > 1
                        ? t("AssetManagement.addMultipleAssetsToGroup")
                        : t("AssetManagement.addAssetToGroup")}
                </Button>
            )}

            {props.isErasureReportSelected && (
                <Tooltip>
                    {props.isPrintDisabled && (
                        <TooltipContent>
                            {`${t("ErasureReport.print.printToolTipMessage", {
                                maxReportLimit: MAX_PRINT_REPORTS_LIMIT,
                            })}.`}
                        </TooltipContent>
                    )}
                    <TooltipTrigger>
                        <Button
                            variant="Tertiary"
                            icon={<Print color={resolveColor("--theme-icon-fill-default")} />}
                            disabled={props.isLoading || props.isPrintDisabled}
                            onClick={() => {
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORTS,
                                    action: Action.PRINT_SELECTED_REPORTS,
                                });
                                props.openPrintModal();
                            }}
                            testId={testIds.common.primaryView.table.actionBar.button}
                            dataType="PRINT"
                        >
                            {t("ErasureReport.print.title")}
                        </Button>
                    </TooltipTrigger>
                </Tooltip>
            )}

            {hasAutopilotRecheckAuthorities && !hasTenantCookie() && props.isReportWithAutopilotStateSelected && (
                <Tooltip>
                    {props.selectionCountOverRecheckLimit ? (
                        <TooltipContent>
                            {t("ErasureReport.recheckLimitReachedMessage", {
                                maxReportLimit: MAX_RECHECK_REPORTS_LIMIT,
                            })}
                        </TooltipContent>
                    ) : !props.hasValidLicensesForRecheck ? (
                        <TooltipContent>{t("ErasureReport.insufficientLicensesMessage")}</TooltipContent>
                    ) : null}
                    <TooltipTrigger>
                        <Button
                            variant="Tertiary"
                            icon={<RefreshIcon color={resolveColor("--theme-icon-fill-default")} />}
                            disabled={
                                props.isLoading ||
                                props.selectionCountOverRecheckLimit ||
                                !props.hasValidLicensesForRecheck
                            }
                            onClick={() => {
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORTS,
                                    action: Action.RECHECK_MULTIPLE_REPORTS,
                                });
                                props.openAutopilotRecheckModal();
                            }}
                            testId={testIds.common.primaryView.table.actionBar.button}
                            dataType="AUTOPILOT_RECHECK"
                        >
                            {t("Common.recheck")}
                        </Button>
                    </TooltipTrigger>
                </Tooltip>
            )}

            {hasDeleteAuthorities && (
                <Button
                    variant="TertiaryDelete"
                    disabled={props.isLoading}
                    icon={<Delete />}
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.REPORTS,
                            action: Action.REMOVE_MULTIPLE_REPORTS,
                        });
                        props.openDeleteModal();
                    }}
                    testId={testIds.common.primaryView.table.actionBar.button}
                    dataType="DELETE"
                >
                    {t("DeleteReport.delete")}
                </Button>
            )}
        </ActionBar>
    );
};
